/**
 * Goods In Transit dashboard.
 *
 * Reads from /api/finance/goods-in-transit and renders:
 *   - Total GIT value (per subsidiary currency where mixed)
 *   - By-supplier table (count + value, sorted by value DESC)
 *   - By-brand table
 *   - By-ETA bucket table (Overdue / This week / Next 2 weeks / 3-4 weeks / >1 month / No ETA)
 *   - Shipment list with drill-down to the underlying PO (link only — no inline detail)
 *
 * Pure read view. Operators record dispatch / receipt elsewhere (PO detail page).
 *
 * Reconciliation tab cross-checks GL 1220 vs derived total.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Grid, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, CircularProgress, Alert,
  Tabs, Tab, IconButton, Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import api from '../../config/api';
import { formatDate } from '../../services/procurementService';

function fmt(value, currency) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const n = Number(value);
  const ccy = currency || '';
  return `${ccy ? ccy + ' ' : ''}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function GoodsInTransit() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [recon, setRecon] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/api/finance/goods-in-transit');
      setData(r.data || null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setLoading(false); }
  }, []);

  const loadRecon = useCallback(async () => {
    setReconLoading(true);
    try {
      const r = await api.get('/api/finance/goods-in-transit/reconciliation');
      setRecon(r.data || null);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setReconLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 1) loadRecon(); }, [tab, loadRecon]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;

  const shipments = data?.shipments || [];
  const total = data?.total_git_value || 0;
  const bySupplier = data?.by_supplier || [];
  const byBrand = data?.by_brand || [];
  const byEta = data?.by_eta_bucket || [];
  const currency = shipments[0]?.currency || '';

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" fontWeight="600">Goods in Transit</Typography>
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
            As of {data?.as_of}
          </Typography>
          <IconButton size="small" onClick={() => { load(); if (tab === 1) loadRecon(); }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Overview" />
        <Tab label="Reconciliation" />
      </Tabs>

      {tab === 0 && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Total GIT value</Typography>
                  <Typography variant="h4" fontWeight="600" sx={{ mt: 0.5 }}>
                    {fmt(total, currency)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Shipments in transit</Typography>
                  <Typography variant="h4" fontWeight="600" sx={{ mt: 0.5 }}>
                    {data?.shipment_count || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card>
                <CardContent>
                  <Typography variant="caption" color="text.secondary">Suppliers represented</Typography>
                  <Typography variant="h4" fontWeight="600" sx={{ mt: 0.5 }}>
                    {bySupplier.length}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>By supplier</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Supplier</TableCell>
                          <TableCell align="right">Shipments</TableCell>
                          <TableCell align="right">Value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bySupplier.length === 0 && (
                          <TableRow><TableCell colSpan={3} align="center"><Typography variant="caption" color="text.secondary">No data</Typography></TableCell></TableRow>
                        )}
                        {bySupplier.map(r => (
                          <TableRow key={r.supplier_id}>
                            <TableCell>{r.supplier_name || `Supplier #${r.supplier_id}`}</TableCell>
                            <TableCell align="right">{r.count}</TableCell>
                            <TableCell align="right">{fmt(r.value, currency)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>By ETA bucket</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ETA</TableCell>
                          <TableCell align="right">Shipments</TableCell>
                          <TableCell align="right">Value</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {byEta.length === 0 && (
                          <TableRow><TableCell colSpan={3} align="center"><Typography variant="caption" color="text.secondary">No data</Typography></TableCell></TableRow>
                        )}
                        {byEta.map(r => (
                          <TableRow key={r.label}>
                            <TableCell>
                              <Chip
                                label={r.label}
                                size="small"
                                color={r.label === 'Overdue' ? 'error' : r.label === 'This week' ? 'warning' : 'default'}
                              />
                            </TableCell>
                            <TableCell align="right">{r.count}</TableCell>
                            <TableCell align="right">{fmt(r.value, currency)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Shipments</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>PO</TableCell>
                          <TableCell>Supplier</TableCell>
                          <TableCell>Dispatched</TableCell>
                          <TableCell>ETA</TableCell>
                          <TableCell>BL / AWB</TableCell>
                          <TableCell>Carrier</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">GIT value</TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {shipments.length === 0 && (
                          <TableRow><TableCell colSpan={9} align="center"><Typography variant="caption" color="text.secondary">No shipments in transit. Mark a planned shipment as dispatched from the PO detail page.</Typography></TableCell></TableRow>
                        )}
                        {shipments.map(s => (
                          <TableRow key={s.shipment_id} hover>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{s.po_number}</TableCell>
                            <TableCell>{s.supplier_name}</TableCell>
                            <TableCell>{formatDate(s.dispatched_at)}</TableCell>
                            <TableCell>{s.eta ? formatDate(s.eta) : '—'}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{s.bill_of_lading || '—'}</TableCell>
                            <TableCell>{s.carrier || '—'}</TableCell>
                            <TableCell><Chip size="small" label={s.status} color={s.status === 'arrived' ? 'info' : 'default'} /></TableCell>
                            <TableCell align="right">{fmt(s.git_value, s.currency)}</TableCell>
                            <TableCell>
                              <Button size="small" onClick={() => navigate(`/procurement/purchase-orders/${s.po_id}`)}>
                                Open PO
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {tab === 1 && (
        <Box>
          {reconLoading && <CircularProgress />}
          {!reconLoading && recon && (
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  GL 1220 (Inventory in Transit) vs derived
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">GL balance</Typography>
                    <Typography variant="h5" fontWeight={600}>{fmt(recon.gl_balance)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">Derived from shipments</Typography>
                    <Typography variant="h5" fontWeight={600}>{fmt(recon.derived_total)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">Variance</Typography>
                    <Typography variant="h5" fontWeight={600} color={recon.reconciled ? 'success.main' : 'error.main'}>
                      {fmt(recon.variance)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Alert severity={recon.reconciled ? 'success' : 'error'} sx={{ mt: 1 }}>
                      {recon.reconciled
                        ? 'Reconciled — GL balance matches the derived total within rounding tolerance (±0.05).'
                        : `Out of balance by ${fmt(recon.variance)}. Inspect recent shipment_dispatch / shipment_receipt journal entries against the shipments list to find the drift.`}
                    </Alert>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}
        </Box>
      )}
    </Box>
  );
}
